import type { DeadLetterEntry, Participant, PoolMode, SigningKey } from '@pf/contracts';
import {
  Inbox,
  KeyRound,
  PauseCircle,
  Pencil,
  Play,
  PlayCircle,
  Plus,
  RefreshCcw,
  ShieldOff,
  X,
} from 'lucide-react';
import { useState, type SyntheticEvent, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import {
  useCreateParticipant,
  useDlq,
  usePayeeAccounts,
  useParticipants,
  useReplayDelivery,
  useRevokeKey,
  useRotateKey,
  useSigningKeys,
  useUpdateParticipant,
} from '@/lib/api/ops-queries';
import { bankInfo, maskAccountNumber } from '@/lib/banks';
import { formatDateTime, formatRelative } from '@/lib/datetime';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { TONE_CLASSES } from '@/lib/states';
import { cn, shortId } from '@/lib/utils';
import { CopyButton } from '@/components/domain/copy-button';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader, Section } from '@/components/layout/page';
import { RequireRole } from '@/routes/guards';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import {
  Code,
  Input,
  Label,
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '@/components/ui/primitives';

/**
 * Admin — participants, key rotation, payee accounts, and the webhook DLQ.
 *
 * Two of these tabs encode a rule that is easy to get wrong and expensive to get wrong:
 *
 * - **Key rotation is overlapping, never a swap.** SAD §11: a participant may hold two active
 *   signing keys; inbound verification accepts either, outbound signing uses the newest. So
 *   rotating *retires* the outgoing key rather than revoking it, and revoking the last active key
 *   is refused — that action would lock a live integration out mid-flight. The UI says so.
 * - **The DLQ is an incident queue.** SAD §13 is explicit. Replay is offered, but with the
 *   warning that a receiver which failed eight times with backoff will usually fail a ninth, and
 *   that the website's own reconciliation feed is the safety net.
 */
export function AdminPage(): ReactNode {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t('admin.title')} description={t('admin.description')} />

      <Tabs defaultValue="participants">
        <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="participants">{t('admin.tabs.participants')}</TabsTrigger>
          <TabsTrigger value="keys">{t('admin.tabs.keys')}</TabsTrigger>
          <TabsTrigger value="payees">{t('admin.tabs.payees')}</TabsTrigger>
          <TabsTrigger value="dlq">{t('admin.tabs.dlq')}</TabsTrigger>
        </TabsList>

        <TabsContent value="participants">
          <ParticipantsTab />
        </TabsContent>
        <TabsContent value="keys">
          <KeysTab />
        </TabsContent>
        <TabsContent value="payees">
          <PayeesTab />
        </TabsContent>
        <TabsContent value="dlq">
          <DlqTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Participants
// ---------------------------------------------------------------------------------------------

/**
 * Are these websites drawing on one pool of withdrawals, or each on its own?
 *
 * Isolated is the calmer arrangement and is reported as such: nothing crosses between websites, so
 * no website can end up owing another. Shared is reported as *attention* rather than as a problem
 * — it is a deliberate capability, and the reason it is not neutral is that it is the state in
 * which a depositor's baht can reach another company's customer, which an operator should never
 * discover by accident.
 *
 * The counterpart case is worth naming out loud: sharing switched on with a single active website
 * looks enabled and behaves exactly like isolation, because there is nobody to share with.
 */
function PoolModeCallout(props: { readonly pool: PoolMode | undefined }): ReactNode {
  const { t } = useTranslation();
  const pool = props.pool;
  if (pool === undefined) return null;

  const alone = pool.shared && pool.participants < 2;
  return (
    <Callout
      tone={pool.shared ? 'warning' : 'info'}
      title={t(
        pool.shared
          ? 'admin.participants.pool.sharedTitle'
          : 'admin.participants.pool.isolatedTitle',
      )}
    >
      <p>
        {t(
          alone
            ? 'admin.participants.pool.sharedAloneBody'
            : pool.shared
              ? 'admin.participants.pool.sharedBody'
              : 'admin.participants.pool.isolatedBody',
          { count: pool.participants },
        )}
      </p>
    </Callout>
  );
}

/**
 * Every participant grouped by its `clearing_group`, plus who has none (a pool of one).
 *
 * Derived purely from the already-fetched participant list — `clearing_group` names are not a
 * directory of their own on the wire, just a string repeated on whichever rows share it. Built
 * once per render and threaded down to the pill, the picker, and the overview, so all three agree
 * on membership and counts without three separate scans of the list disagreeing after an edit.
 */
interface PoolIndex {
  /** Pool name → its member participants, in list order. */
  readonly byPool: ReadonlyMap<string, Participant[]>;
  /** Participants with `clearing_group === null` (or omitted): a pool of one. */
  readonly solo: readonly Participant[];
}

function buildPoolIndex(items: readonly Participant[]): PoolIndex {
  const byPool = new Map<string, Participant[]>();
  const solo: Participant[] = [];
  for (const p of items) {
    const group = p.clearing_group ?? null;
    if (group === null) {
      solo.push(p);
      continue;
    }
    const members = byPool.get(group);
    if (members) members.push(p);
    else byPool.set(group, [p]);
  }
  return { byPool, solo };
}

/**
 * One participant's clearing pool: its `clearing_group` name plus member count, or "pool of one"
 * when `null`.
 *
 * Two rows sharing a group render the identical pill text — the only signal an operator needs to
 * see who cross-matches with whom. No color-per-group scheme: a repeated name already says "these
 * share a pool" without one, and inventing a color mapping would be one more thing to keep in sync
 * as pools are renamed. The count in parentheses is new: it turns "th-pool-1" from a name an
 * operator has to go count rows to size up into a number they can read at a glance — and it is the
 * same number the pools overview above the table shows for that pool.
 *
 * ADMIN (and SUPER, which `can('ADMIN')` already covers per the no-hierarchy-except-SUPER rule)
 * can edit inline, via the same `useUpdateParticipant` mutation the status button already uses.
 *
 * Editing offers a **picker**, not a bare text field: joining an existing pool is a selection from
 * the pool names already in use elsewhere in the fetched list (`poolIndex`, minus this
 * participant's own current group), never typing. Free text only reappears behind an explicit
 * "create new pool" affordance — the one place a genuinely new name is meant to be typed. This is
 * what keeps a typo like "th-pool-l" for "th-pool-1" from silently founding its own pool: the
 * operator can no longer fat-finger an existing name, only choose it or knowingly create a new one.
 * Saving a blank field, or clicking "Remove from pool", sends explicit `clearing_group: null` — the
 * "move back to a pool of one" case the wire contract requires to be reachable and distinct from
 * not touching the field.
 */
function ParticipantPoolCell(props: {
  readonly participant: Participant;
  readonly poolIndex: PoolIndex;
}): ReactNode {
  const { t } = useTranslation();
  const { can } = useAuth();
  const update = useUpdateParticipant();
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [value, setValue] = useState(props.participant.clearing_group ?? '');

  // `?? null`: the wire field is optional for deploy-order compatibility — an API that predates
  // clearing pools omits it, and an omitted pool is a pool of one.
  const group = props.participant.clearing_group ?? null;
  const isPending = update.isPending && update.variables?.id === props.participant.id;
  const memberCount = group === null ? 0 : (props.poolIndex.byPool.get(group)?.length ?? 1);

  // Every other named pool in the fetched list — the picker's choices. This participant's own
  // current group is excluded: selecting it again would be a no-op dressed up as a choice.
  const otherPools = [...props.poolIndex.byPool.keys()]
    .filter((name) => name !== group)
    .sort((a, b) => a.localeCompare(b));

  const badge =
    group === null ? (
      <Pill>{t('admin.participants.pool.aloneBadge')}</Pill>
    ) : (
      <Pill className={TONE_CLASSES.flight.badge}>
        {t('admin.participants.pool.pillWithCount', { group, count: memberCount })}
      </Pill>
    );

  if (!can('ADMIN')) return badge;

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        {badge}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setValue(group ?? '');
            // Nothing to pick from yet (no other named pool exists) skips straight to the
            // free-text "create new" form — offering an empty picker would just be a dead click.
            setCreating(otherPools.length === 0);
            setEditing(true);
          }}
        >
          <Pencil className="size-3" aria-hidden="true" />
          <span className="sr-only">
            {t('admin.participants.pool.editLabel', { name: props.participant.name })}
          </span>
        </Button>
      </div>
    );
  }

  const removeButton =
    group !== null ? (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={isPending}
        onClick={() => {
          update.mutate(
            { id: props.participant.id, clearing_group: null },
            {
              onSuccess: () => {
                setEditing(false);
              },
            },
          );
        }}
      >
        <X className="size-3" aria-hidden="true" />
        {t('admin.participants.pool.removeButton')}
      </Button>
    ) : null;

  const cancelButton = (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      disabled={isPending}
      onClick={() => {
        setEditing(false);
      }}
    >
      {t('common.cancel')}
    </Button>
  );

  if (!creating) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Select
          onValueChange={(nextGroup) => {
            update.mutate(
              { id: props.participant.id, clearing_group: nextGroup },
              {
                onSuccess: () => {
                  setEditing(false);
                },
              },
            );
          }}
        >
          <SelectTrigger
            className="h-7 w-36 text-xs"
            aria-label={t('admin.participants.pool.pickLabel')}
            disabled={isPending}
          >
            <SelectValue placeholder={t('admin.participants.pool.pickPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {otherPools.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isPending}
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus className="size-3" aria-hidden="true" />
          {t('admin.participants.pool.createNewButton')}
        </Button>
        {removeButton}
        {cancelButton}
      </div>
    );
  }

  return (
    <form
      className="flex flex-wrap items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = value.trim();
        update.mutate(
          { id: props.participant.id, clearing_group: trimmed === '' ? null : trimmed },
          {
            onSuccess: () => {
              setEditing(false);
            },
          },
        );
      }}
    >
      <Input
        aria-label={t('admin.participants.pool.inputLabel')}
        className="h-7 w-28 text-xs"
        value={value}
        placeholder={t('admin.participants.pool.inputPlaceholder')}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        autoFocus
      />
      <Button type="submit" size="sm" variant="ghost" loading={isPending}>
        {t('admin.participants.pool.saveButton')}
      </Button>
      {otherPools.length > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isPending}
          onClick={() => {
            setCreating(false);
          }}
        >
          {t('admin.participants.pool.backToPickButton')}
        </Button>
      ) : null}
      {removeButton}
      {cancelButton}
    </form>
  );
}

/**
 * Which clearing pools exist, who is in each, and which websites are a pool of one — derived
 * purely from the fetched participant list, above the table so an operator sees the shape of the
 * pools before scanning rows for it.
 *
 * A pool with exactly one member is flagged, not blocked: a clearing pool matches deposits and
 * withdrawals *across* participants, so a lone member has nobody to cross-match with. That is very
 * often a typo that founded a near-miss pool instead of joining the intended one — the picker in
 * the table forecloses that for *new* joins, but a pool can still land here from data seeded,
 * imported, or edited before the picker existed. Amber, with a plain-language hint, rather than an
 * error: a genuinely solo pool (a website onboarding ahead of its future pool-mates) is a real and
 * unremarkable state, not a fault.
 */
function PoolsOverview(props: { readonly poolIndex: PoolIndex }): ReactNode {
  const { t } = useTranslation();
  const pools = [...props.poolIndex.byPool.entries()].sort(([a], [b]) => a.localeCompare(b));

  if (pools.length === 0 && props.poolIndex.solo.length === 0) return null;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium">{t('admin.participants.pool.overview.title')}</h3>
        <p className="text-xs text-muted-foreground">
          {t('admin.participants.pool.overview.description')}
        </p>
      </div>

      {pools.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pools.map(([name, members]) => {
            const alone = members.length === 1;
            return (
              <Card
                key={name}
                className={alone ? 'border-state-attention/30 bg-state-attention-bg/30' : undefined}
              >
                <CardHeader className="space-y-1 pb-2">
                  <CardTitle className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{name}</span>
                    <span className="tnum shrink-0 text-2xs font-normal text-muted-foreground">
                      {t('admin.participants.pool.overview.memberCount', {
                        count: members.length,
                      })}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <ul className="flex flex-wrap gap-1.5">
                    {members.map((member) => (
                      <li key={member.id}>
                        <Pill
                          className={
                            member.status === 'ACTIVE' ? TONE_CLASSES.good.badge : undefined
                          }
                        >
                          {member.id}
                        </Pill>
                      </li>
                    ))}
                  </ul>
                  {alone ? (
                    <p className="text-2xs text-state-attention">
                      {t('admin.participants.pool.overview.aloneHint')}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}

      {props.poolIndex.solo.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {t('admin.participants.pool.overview.soloLabel')}
          </span>{' '}
          {props.poolIndex.solo.map((p) => p.id).join(', ')}
        </p>
      ) : null}
    </div>
  );
}

function ParticipantsTab(): ReactNode {
  const { t } = useTranslation();
  const participants = useParticipants();
  const update = useUpdateParticipant();
  const create = useCreateParticipant();
  const { can } = useAuth();
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const poolIndex = buildPoolIndex(participants.data?.items ?? []);

  const columns: Column<Participant>[] = [
    {
      id: 'name',
      header: t('admin.participants.columns.participant'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.name}</p>
          <Code className="mt-0.5 inline-block">{row.id}</Code>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('admin.participants.columns.status'),
      priority: 'secondary',
      cell: (row) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-medium',
            row.status === 'ACTIVE'
              ? 'bg-state-good-bg text-state-good'
              : 'bg-state-bad-bg text-state-bad',
          )}
        >
          {row.status === 'ACTIVE' ? (
            <PlayCircle className="size-3" aria-hidden="true" />
          ) : (
            <PauseCircle className="size-3" aria-hidden="true" />
          )}
          {row.status === 'ACTIVE'
            ? t('admin.participants.statusActive')
            : t('admin.participants.statusSuspended')}
        </span>
      ),
    },
    {
      id: 'pool',
      header: t('admin.participants.columns.pool'),
      priority: 'secondary',
      cell: (row) => <ParticipantPoolCell participant={row} poolIndex={poolIndex} />,
    },
    {
      id: 'created',
      header: t('admin.participants.columns.onboarded'),
      priority: 'detail',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: t('admin.participants.columns.actionsSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={!can('ADMIN')}
          loading={update.isPending && update.variables?.id === row.id}
          onClick={() => {
            update.mutate({
              id: row.id,
              status: row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
            });
          }}
        >
          {row.status === 'ACTIVE' ? (
            <PauseCircle aria-hidden="true" />
          ) : (
            <Play aria-hidden="true" />
          )}
          {row.status === 'ACTIVE'
            ? t('admin.participants.suspend')
            : t('admin.participants.reactivate')}
        </Button>
      ),
    },
  ];

  const onCreate = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (newId.trim() === '' || newName.trim() === '') return;
    create.mutate(
      { id: newId.trim(), name: newName.trim() },
      {
        onSuccess: () => {
          setNewId('');
          setNewName('');
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <Section
        title={t('admin.participants.sectionTitle')}
        description={t('admin.participants.sectionDescription')}
      >
        {participants.isLoading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : participants.isError ? (
          <ErrorState
            error={participants.error}
            onRetry={() => {
              void participants.refetch();
            }}
          />
        ) : (
          <>
            {/*
              Whether a website's depositors are matched against its own withdrawals or against
              everybody's. It decides who ends up owing whom, and the directory could not answer it
              at all — an operator had to open the Clearing page and infer it from a badge.

              Stated once above the table rather than as a column, because one policy value moves
              every website together: a per-row cell would read as a per-website setting and send
              somebody looking for a switch that does not exist.
            */}
            <PoolModeCallout pool={participants.data?.pool} />
            <PoolsOverview poolIndex={poolIndex} />
            {update.isError ? <ErrorState error={update.error} /> : null}
            <DataTable
              caption={t('admin.participants.tableCaption')}
              columns={columns}
              rows={participants.data?.items ?? []}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  title={t('admin.participants.emptyTitle')}
                  description={t('admin.participants.emptyDescription')}
                />
              }
            />
          </>
        )}
      </Section>

      <RequireRole role="ADMIN" fallback={null}>
        <Section title={t('admin.participants.registerSectionTitle')}>
          <Card className="max-w-xl">
            <CardContent className="pt-5">
              <form className="space-y-4" onSubmit={onCreate} noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="participant-id">{t('admin.participants.idLabel')}</Label>
                    <Input
                      id="participant-id"
                      value={newId}
                      placeholder={t('admin.participants.idPlaceholder')}
                      onChange={(event) => {
                        setNewId(event.target.value);
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="participant-name">{t('admin.participants.nameLabel')}</Label>
                    <Input
                      id="participant-name"
                      value={newName}
                      placeholder={t('admin.participants.namePlaceholder')}
                      onChange={(event) => {
                        setNewName(event.target.value);
                      }}
                    />
                  </div>
                </div>
                {create.isError ? <ErrorState error={create.error} /> : null}
                <Button type="submit" loading={create.isPending}>
                  <Plus aria-hidden="true" />
                  {t('admin.participants.registerButton')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Section>
      </RequireRole>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Signing keys
// ---------------------------------------------------------------------------------------------

function KeysTab(): ReactNode {
  const { t } = useTranslation();
  const participants = useParticipants();
  // This tab keeps a picker of its own rather than following the console-wide scope, unlike every
  // other read on this page — deliberately. The scope answers "which website's data am I looking
  // at", a view filter; this picker answers "whose keys am I about to rotate or revoke", a
  // one-time act with a side effect that reaches a real integration. Collapsing the two would mean
  // a stray scope switch changes which participant a pending rotate click lands on.
  //
  // It still *opens* on the console's scope when one is set — an operator who scoped the console
  // to a website almost always came here to act on that website's keys, and typing the id again is
  // a chance to fat-finger a participant that then rotates the wrong one's keys. Read once, at
  // mount, via the lazy initializer: switching the console scope afterward must not yank the
  // selection out from under an operator mid-task.
  const { scope } = useWebScope();
  const [selected, setSelected] = useState(() => (scope === ALL_WEBS ? 'web_a' : scope));
  const keys = useSigningKeys(selected);
  const rotate = useRotateKey(selected);
  const revoke = useRevokeKey(selected);
  const { can } = useAuth();
  const [revealed, setRevealed] = useState<{ keyId: string; secret: string } | null>(null);

  // Counted **per purpose**, because that is the rule the server enforces: a participant must keep
  // at least one active key in each direction. Counting across both would enable the revoke button
  // on a participant's only inbound key merely because an outbound one exists — a click that fails
  // at the API and teaches the operator to distrust the button.
  const activeInPurpose = (purpose: SigningKey['purpose']): number =>
    (keys.data?.items ?? []).filter((k) => k.status === 'ACTIVE' && k.purpose === purpose).length;

  const statusTone: Record<SigningKey['status'], string> = {
    ACTIVE: 'bg-state-good-bg text-state-good',
    RETIRING: 'bg-state-attention-bg text-state-attention',
    REVOKED: 'bg-state-idle-bg text-state-idle',
  };

  return (
    <div className="space-y-6">
      <Section
        title={t('admin.keys.sectionTitle')}
        description={t('admin.keys.sectionDescription')}
        actions={
          <div className="flex items-end gap-2">
            <div className="w-48 space-y-1.5">
              <Label htmlFor="key-participant">{t('admin.keys.participantLabel')}</Label>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger id="key-participant">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(participants.data?.items ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              disabled={!can('ADMIN')}
              loading={rotate.isPending}
              onClick={() => {
                rotate.mutate(undefined, {
                  onSuccess: (result) => {
                    setRevealed({ keyId: result.key_id, secret: result.secret });
                  },
                });
              }}
            >
              <RefreshCcw aria-hidden="true" />
              {t('admin.keys.rotateButton')}
            </Button>
          </div>
        }
      >
        {revealed ? (
          <Callout
            tone="warning"
            title={t('admin.keys.revealedTitle', { keyId: revealed.keyId })}
          >
            <div className="space-y-2">
              <p>{t('admin.keys.revealedBody')}</p>
              <div className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
                <code className="min-w-0 flex-1 break-all font-mono text-xs">
                  {revealed.secret}
                </code>
                <CopyButton
                  value={revealed.secret}
                  label={t('admin.keys.copySecretLabel')}
                  size="sm"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRevealed(null);
                }}
              >
                {t('admin.keys.storedButton')}
              </Button>
            </div>
          </Callout>
        ) : null}

        {rotate.isError ? <ErrorState error={rotate.error} /> : null}
        {revoke.isError ? <ErrorState error={revoke.error} /> : null}

        {keys.isLoading ? (
          <TableSkeleton rows={3} columns={3} />
        ) : (
          <ul className="space-y-2">
            {(keys.data?.items ?? []).map((key) => (
              <li
                key={key.key_id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
                    <Code className="text-sm">{key.key_id}</Code>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-2xs font-medium',
                        statusTone[key.status],
                      )}
                    >
                      {key.status.toLowerCase()}
                    </span>
                    {/* The direction, spelled out. Key ids are opaque and the list mixes both, so
                        without this an operator cannot tell which key their integration holds. */}
                    <span className="rounded-full bg-muted px-2 py-0.5 text-2xs font-medium text-muted-foreground">
                      {t(`admin.keys.purpose.${key.purpose}`)}
                    </span>
                  </div>
                  <p className="text-2xs text-muted-foreground">
                    created {formatRelative(key.created_at)} · {formatDateTime(key.created_at)}
                  </p>
                </div>
                <Tooltip
                  content={
                    key.status === 'REVOKED'
                      ? t('admin.keys.tooltipRevoked')
                      : key.status === 'ACTIVE' && activeInPurpose(key.purpose) === 1
                        ? t('admin.keys.tooltipLastActive')
                        : t('admin.keys.tooltipRevoke')
                  }
                >
                  <span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={
                        !can('ADMIN') ||
                        key.status === 'REVOKED' ||
                        (key.status === 'ACTIVE' && activeInPurpose(key.purpose) === 1)
                      }
                      loading={revoke.isPending && revoke.variables === key.key_id}
                      onClick={() => {
                        revoke.mutate(key.key_id);
                      }}
                    >
                      <ShieldOff aria-hidden="true" />
                      {t('admin.keys.revokeButton')}
                    </Button>
                  </span>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Payee accounts
// ---------------------------------------------------------------------------------------------

function PayeesTab(): ReactNode {
  const { t } = useTranslation();
  const payees = usePayeeAccounts();

  return (
    <Section
      title={t('admin.payees.sectionTitle')}
      description={t('admin.payees.sectionDescription')}
    >
      {payees.isLoading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : payees.isError ? (
        <ErrorState
          error={payees.error}
          onRetry={() => {
            void payees.refetch();
          }}
        />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {(payees.data?.items ?? []).map((payee) => {
            const bank = bankInfo(payee.account.bank);
            return (
              <li key={payee.id}>
                <Card>
                  <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className="flex size-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                        style={{ backgroundColor: bank.color }}
                        aria-hidden="true"
                      >
                        {bank.monogram}
                      </span>
                      <div className="min-w-0">
                        <CardTitle className="truncate">{payee.account.name}</CardTitle>
                        <p className="tnum truncate pt-0.5 font-mono text-2xs text-muted-foreground">
                          {maskAccountNumber(payee.account.number)} · {bank.name}
                        </p>
                      </div>
                    </div>
                    <Pill
                      className={
                        payee.status === 'ACTIVE'
                          ? 'bg-state-good-bg text-state-good'
                          : 'bg-state-idle-bg text-state-idle'
                      }
                    >
                      {payee.status.toLowerCase()}
                    </Pill>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('admin.payees.aliasesTitle')}
                    </p>
                    <ul className="flex flex-wrap gap-1.5">
                      {payee.aliases.map((alias) => (
                        <li key={alias}>
                          <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-2xs">
                            {alias}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-1 text-2xs text-muted-foreground">
                      <Trans
                        i18nKey="admin.payees.participantPrefix"
                        values={{ id: payee.participant }}
                        components={{ code: <Code /> }}
                      />
                    </p>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// Webhook DLQ
// ---------------------------------------------------------------------------------------------

function DlqTab(): ReactNode {
  const { t } = useTranslation();
  // No picker of its own any more: the dead-letter queue follows the console-wide website scope
  // in the shell, like every other operator read. The one it used to have sent the literal string
  // `ALL` on the wire for "all participants", which the API reads as a participant *named* `ALL` —
  // so against a real server the tab's default view showed an empty queue rather than every entry.
  const dlq = useDlq();
  const replay = useReplayDelivery();

  const columns: Column<DeadLetterEntry>[] = [
    {
      id: 'event',
      header: t('admin.dlq.columns.event'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm font-medium">{row.event_type}</p>
          <p className="truncate font-mono text-2xs text-muted-foreground">
            {shortId(row.delivery_id)} · {shortId(row.event_id)}
          </p>
        </div>
      ),
    },
    {
      id: 'participant',
      header: t('admin.dlq.columns.participant'),
      priority: 'secondary',
      cell: (row) => <Code>{row.participant}</Code>,
    },
    {
      id: 'attempts',
      header: t('admin.dlq.columns.attempts'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum text-xs">{row.attempts}</span>,
    },
    {
      id: 'error',
      header: t('admin.dlq.columns.lastError'),
      priority: 'detail',
      cell: (row) => (
        <span
          className="block max-w-md truncate text-xs text-muted-foreground"
          title={row.last_error ?? ''}
        >
          {row.last_error ?? '—'}
        </span>
      ),
    },
    {
      id: 'when',
      header: t('admin.dlq.columns.deadLettered'),
      priority: 'detail',
      cell: (row) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatRelative(row.dead_lettered_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: t('admin.dlq.columns.actionsSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => (
        <Button
          variant="outline"
          size="sm"
          loading={replay.isPending && replay.variables === row.delivery_id}
          onClick={() => {
            replay.mutate(row.delivery_id);
          }}
        >
          <RefreshCcw aria-hidden="true" />
          {t('admin.dlq.replayButton')}
        </Button>
      ),
    },
  ];

  const items = dlq.data?.items ?? [];

  return (
    <Section
      title={t('admin.dlq.sectionTitle')}
      description={t('admin.dlq.sectionDescription')}
    >
      <div className="space-y-4">
        {items.length > 0 ? (
          <Callout tone="danger" title={t('admin.dlq.incidentTitle')}>
            <Trans
              i18nKey="admin.dlq.incidentBody"
              values={{ code: 'GET /transactions?updated_since' }}
              components={{ code: <Code /> }}
            />
          </Callout>
        ) : null}

        {replay.isError ? <ErrorState error={replay.error} /> : null}

        {dlq.isLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : dlq.isError ? (
          <ErrorState
            error={dlq.error}
            onRetry={() => {
              void dlq.refetch();
            }}
          />
        ) : (
          <DataTable
            caption={t('admin.dlq.tableCaption')}
            columns={columns}
            rows={items}
            rowKey={(row) => row.delivery_id}
            empty={
              <EmptyState
                icon={<Inbox className="size-5" aria-hidden="true" />}
                title={t('admin.dlq.emptyTitle')}
                description={t('admin.dlq.emptyDescription')}
              />
            }
          />
        )}
      </div>
    </Section>
  );
}
